import { useState, useEffect, useRef } from 'react'

type UseInfiniteRevealOptions = {
  initialCount?: number
  onCountChange?: (count: number) => void
}

export function useInfiniteReveal<T>(
  items: T[],
  pageSize: number,
  resetKey?: unknown,
  options?: UseInfiniteRevealOptions,
) {
  const [visibleCount, setVisibleCount] = useState(options?.initialCount ?? pageSize)
  const sentinelRef = useRef<HTMLDivElement | null>(null)
  const isFirstRenderRef = useRef(true)

  // Latest-callback ref (same pattern as getKeyRef in useSortOrder.ts) — avoids re-running
  // the effect below just because the caller passed a fresh inline arrow function.
  const onCountChangeRef = useRef(options?.onCountChange)
  onCountChangeRef.current = options?.onCountChange

  useEffect(() => {
    // Skip on mount — the initial value already came from `initialCount` (e.g. restored
    // from a URL param); only a later change to resetKey should collapse back to pageSize.
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false
      return
    }
    setVisibleCount(pageSize)
  }, [resetKey, pageSize])

  useEffect(() => {
    onCountChangeRef.current?.(visibleCount)
  }, [visibleCount])

  const loadMore = () => setVisibleCount((prev) => Math.min(prev + pageSize, items.length))

  useEffect(() => {
    const node = sentinelRef.current
    if (!node) return

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) loadMore()
    })
    observer.observe(node)

    // Safety net for React 18 StrictMode's dev-only mount→cleanup→mount double-invoke:
    // when the sentinel is already on-screen the instant the effect attaches (e.g. right
    // after client-side back-navigation, where data is already in the store so the list
    // renders full-height from the first paint), the observer's async initial callback can
    // race with the disconnect from the first invocation and never get delivered. A direct,
    // synchronous visibility check here doesn't depend on that callback's timing at all.
    const rect = node.getBoundingClientRect()
    if (rect.top < window.innerHeight && rect.bottom > 0) {
      loadMore()
    }

    return () => observer.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length, pageSize])

  return {
    visibleItems: items.slice(0, visibleCount),
    hasMore: visibleCount < items.length,
    sentinelRef,
    // Manual fallback for whenever the IntersectionObserver-driven auto-reveal doesn't fire
    // (e.g. an unusual scroll-container setup on a particular device/browser).
    loadMore,
  }
}
